import type { MetricDef, MetricRecipe } from './types'

/**
 * Backend recipes keyed by metric id. The info icon on each card opens this.
 *
 * Bind: :from timestamptz, :to timestamptz, :org_id int NULL
 * Org filter: JOIN data_projects p ON … AND (:org_id IS NULL OR p.organization_id = :org_id)
 *
 * Client roles: CLIENT, CLIENT_GUEST, GUEST, STAKEHOLDER, PARTICIPANT
 * PM roles: OWNER, TEAM_LEADER
 */

const CLIENT = `'CLIENT','CLIENT_GUEST','GUEST','STAKEHOLDER','PARTICIPANT'`
const PM = `'OWNER','TEAM_LEADER'`

function R(
  returns: string,
  steps: string[],
  sql: string,
  extra: Partial<Pick<MetricRecipe, 'numerator' | 'denominator'>> = {},
): MetricRecipe {
  return { returns, steps, sql, ...extra }
}

const RECIPES: Record<string, MetricRecipe> = {
  client_questions: R(
    'integer count',
    [
      'Window filter: data_mo_conversation_messages.created_at BETWEEN :from AND :to.',
      `Author is a client: messages.role = 'user'. Join user_id → data_user_roles (same project as data_mo_threads.project_id) → cat_roles.name IN (${CLIENT}).`,
      'That message is a QUESTION, not a change request: there is NO data_mo_tasks row with the same run_uuid (or created within 2 minutes of the message) and no new data_tickets row created from that run.',
      'Count distinct message id (not threads). Return COUNT(*).',
    ],
    `-- client_questions
SELECT COUNT(*)::int AS value
FROM data_mo_conversation_messages m
JOIN data_mo_threads t ON t.id = m.thread_id
JOIN data_projects p ON p.id = t.project_id
JOIN data_user_roles ur ON ur.user_id = m.user_id AND ur.project_id = p.id
JOIN cat_roles r ON r.id = ur.role_id
WHERE m.role = 'user'
  AND m.created_at >= :from AND m.created_at < :to
  AND (:org_id IS NULL OR p.organization_id = :org_id)
  AND r.name IN (${CLIENT})
  AND NOT EXISTS (
    SELECT 1 FROM data_mo_tasks tk
    WHERE tk.thread_id = m.thread_id
      AND tk.run_uuid IS NOT DISTINCT FROM m.run_uuid
      AND tk.ticket_id IS NOT NULL
  );`,
    { numerator: 'Client-authored Mo user messages in the window that did not create a ticket/task' },
  ),

  ai_question_resolution_rate: R(
    'float 0..1 (percent)',
    [
      'Denominator = client_questions (same definition).',
      'A question is RESOLVED BY AI when, after the client message, the thread gets an assistant kind IN (text, summary) and NEVER: memory_json.continuationIntent = escalate_human; Mo metadata askKind = escalation_pushback; a PM-role user message; a TEAM/OWNER assignee user_started_at on the linked ticket before resolution.',
      'Resolution timestamp = first such assistant message after the question. Unresolved questions (still open at :to) are excluded from the numerator and stay in the denominator — or exclude both from the rate if you only want closed questions (document which). Recommended: closed-only: require an assistant reply and no later client restatement on a new thread.',
      'Return numerator::float / NULLIF(denominator, 0).',
    ],
    `-- ai_question_resolution_rate
-- CTE questions = client_questions set (ids).
-- Flag escalated if objective memory or PM touch exists on that thread before :to.
WITH questions AS ( /* reuse client_questions ids */ SELECT m.id, m.thread_id FROM … ),
escalated AS (
  SELECT DISTINCT q.id
  FROM questions q
  JOIN data_mo_objective_memories om ON om.thread_id = q.thread_id
  WHERE om.memory_json->>'continuationIntent' = 'escalate_human'
     OR om.memory_json->>'blockedOnUser' = 'true'
)
SELECT COUNT(*) FILTER (WHERE e.id IS NULL)::float
     / NULLIF(COUNT(*), 0) AS value
FROM questions q
LEFT JOIN escalated e ON e.id = q.id;`,
    {
      numerator: 'Client questions with no human handoff before resolution',
      denominator: 'client_questions',
    },
  ),

  human_escalation_rate: R(
    'float 0..1 — should equal 1 − ai_question_resolution_rate on the same closed set',
    [
      'Use the same question set as ai_question_resolution_rate.',
      'Escalated if ANY of: data_mo_objective_memories.memory_json.continuationIntent = escalate_human; data_mo_tasks.phase IN (needs_attention, awaiting_input) after an agent error; a collaborator session status = awaiting_user created from Mo (mo_thread_id set).',
      'Return escalated / questions. Do not double-count a thread with two flags.',
    ],
    `-- human_escalation_rate
SELECT COUNT(*) FILTER (WHERE escalated)::float / NULLIF(COUNT(*), 0)
FROM question_flags; -- see ai_question_resolution_rate`,
    { numerator: 'Questions handed to PM/team', denominator: 'client_questions' },
  ),

  time_to_first_useful_response: R(
    'median duration milliseconds',
    [
      'Grain: data_mo_threads (one number per thread in window, then median).',
      't0 = MIN(created_at) WHERE role=user on that thread.',
      't1 = MIN(created_at) WHERE role=assistant AND kind IN (text, summary, plan) AND length(trim(content)) >= 80 AND content NOT ILIKE all of: gathering clues%, thinking%, please wait%. Skip kind=error.',
      'Sample: t1 − t0. Drop threads with no t1 yet. Return percentile_cont(0.5).',
    ],
    `-- time_to_first_useful_response
WITH firsts AS (
  SELECT t.id,
    MIN(m.created_at) FILTER (WHERE m.role = 'user') AS t0,
    MIN(m.created_at) FILTER (
      WHERE m.role = 'assistant'
        AND m.kind IN ('text','summary','plan')
        AND length(trim(m.content)) >= 80
        AND m.content !~* '^(gathering clues|thinking|please wait)'
    ) AS t1
  FROM data_mo_threads t
  JOIN data_mo_conversation_messages m ON m.thread_id = t.id
  JOIN data_projects p ON p.id = t.project_id
  WHERE (:org_id IS NULL OR p.organization_id = :org_id)
    AND t.created_at >= :from AND t.created_at < :to
  GROUP BY t.id
)
SELECT (EXTRACT(EPOCH FROM percentile_cont(0.5) WITHIN GROUP (ORDER BY t1 - t0)) * 1000)::bigint
FROM firsts WHERE t0 IS NOT NULL AND t1 IS NOT NULL AND t1 >= t0;`,
    { numerator: 'Median (first substantive assistant − first user message) per thread' },
  ),

  time_to_resolution: R(
    'median duration milliseconds',
    [
      'Two populations; compute both, then union for the headline (or split later).',
      'QUESTIONS: resolved_at = last assistant message on the thread where kind IN (text, summary) and no later user message, OR objective status=complete. Start = first user message. Skip still-open threads.',
      'CHANGE REQUESTS: start = data_requests.created_at. End = COALESCE(delivery_on, updated_at) WHERE status = COMPLETED.',
      'Return median of (end − start) in ms.',
    ],
    `-- time_to_resolution (change requests)
SELECT (EXTRACT(EPOCH FROM percentile_cont(0.5) WITHIN GROUP (
  ORDER BY COALESCE(r.delivery_on, r.updated_at) - r.created_at
)) * 1000)::bigint
FROM data_requests r
JOIN data_projects p ON p.id = r.project_id
WHERE r.status = 'COMPLETED'
  AND COALESCE(r.delivery_on, r.updated_at) >= :from
  AND COALESCE(r.delivery_on, r.updated_at) < :to
  AND (:org_id IS NULL OR p.organization_id = :org_id);`,
    { numerator: 'Median (resolved_at − created_at)' },
  ),

  requests_created_through_chat: R(
    'float 0..1',
    [
      'Denominator: COUNT data_requests WHERE created_at in window (org filter).',
      'Chat-originated: request.uuid IN (SELECT request_uuid FROM data_mo_tasks WHERE request_uuid IS NOT NULL) OR request.uuid IN (SELECT entity_uuid FROM data_mo_thread_entities WHERE entity_type = \'deliverable\'). There is NO origin column — this join IS the origin.',
      'Return chat_origin / NULLIF(all_created, 0).',
    ],
    `-- requests_created_through_chat
WITH created AS (
  SELECT r.uuid
  FROM data_requests r
  JOIN data_projects p ON p.id = r.project_id
  WHERE r.created_at >= :from AND r.created_at < :to
    AND (:org_id IS NULL OR p.organization_id = :org_id)
),
from_chat AS (
  SELECT uuid FROM created
  WHERE uuid IN (SELECT request_uuid FROM data_mo_tasks WHERE request_uuid IS NOT NULL)
     OR uuid IN (SELECT entity_uuid FROM data_mo_thread_entities WHERE entity_type = 'deliverable')
)
SELECT COUNT(*)::float / NULLIF((SELECT COUNT(*) FROM created), 0)
FROM from_chat;`,
    { numerator: 'Requests linked to a Mo task or thread entity', denominator: 'Requests created in window' },
  ),

  repeat_mo_usage_30d: R(
    'float 0..1',
    [
      'Cohort: distinct client user_id with a role=user Mo message in [:from, :to).',
      'Returned: that user has another role=user message with created_at > first_message AND created_at <= first_message + interval 30 day (can extend past :to).',
      'Return returned / cohort. (7-day metric is the same with 7 days.)',
    ],
    `-- repeat_mo_usage_30d
WITH first_hit AS (
  SELECT m.user_id, MIN(m.created_at) AS first_at
  FROM data_mo_conversation_messages m
  JOIN data_mo_threads t ON t.id = m.thread_id
  JOIN data_projects p ON p.id = t.project_id
  JOIN data_user_roles ur ON ur.user_id = m.user_id AND ur.project_id = p.id
  JOIN cat_roles r ON r.id = ur.role_id
  WHERE m.role = 'user' AND r.name IN (${CLIENT})
    AND m.created_at >= :from AND m.created_at < :to
    AND (:org_id IS NULL OR p.organization_id = :org_id)
  GROUP BY m.user_id
)
SELECT COUNT(*) FILTER (WHERE EXISTS (
  SELECT 1 FROM data_mo_conversation_messages m2
  WHERE m2.user_id = f.user_id AND m2.role = 'user'
    AND m2.created_at > f.first_at
    AND m2.created_at <= f.first_at + INTERVAL '30 days'
))::float / NULLIF(COUNT(*), 0)
FROM first_hit f;`,
    { numerator: 'Cohort clients who sent another Mo message within 30 days', denominator: 'Client users who asked in the window' },
  ),

  context_preserved_escalation_rate: R(
    'float 0..1 — this is the “82% arrived with investigation already done” metric',
    [
      'Denominator: distinct thread_id or objective_id where continuationIntent = escalate_human OR phase needs_attention in window.',
      'Preserved: at or before the escalation timestamp, the linked ticket has a data_agent_documents row (Detective/planner) OR data_ai_agent_runs.agent_key = detective AND (signals->>root_cause_found) IS NOT NULL OR memory_json contains specialist remaining/findings keys.',
      'Return preserved / escalations.',
    ],
    `-- context_preserved_escalation_rate
WITH esc AS (
  SELECT om.id, om.thread_id, om.primary_ticket_uuid, om.updated_at
  FROM data_mo_objective_memories om
  JOIN data_projects p ON p.id = om.project_id
  WHERE om.memory_json->>'continuationIntent' = 'escalate_human'
    AND om.updated_at >= :from AND om.updated_at < :to
    AND (:org_id IS NULL OR p.organization_id = :org_id)
)
SELECT COUNT(*) FILTER (WHERE EXISTS (
  SELECT 1 FROM data_tickets tk
  JOIN data_agent_documents d ON d.ticket_id = tk.id
  WHERE tk.uuid = e.primary_ticket_uuid
    AND d.created_at <= e.updated_at
    AND d.collaborator_key IN ('detective','planner','security','business_architect')
))::float / NULLIF(COUNT(*), 0)
FROM esc e;`,
    { numerator: 'Escalations that already have a specialist document/findings', denominator: 'Human escalations' },
  ),

  client_first_review_acceptance: R(
    'float 0..1',
    [
      'Grain: data_requests that had at least one PREVIEW_READY (or FEEDBACK_REQUESTED) in/before window.',
      'First-pass accept: the first CHANGE_APPROVED for that request_uuid has NO second PREVIEW_READY between first PREVIEW_READY and that approval.',
      'Use data_project_activity_events.event_type and created_at ordered per request_uuid.',
      'Return first_pass / requests_with_preview_review.',
    ],
    `-- client_first_review_acceptance
WITH ev AS (
  SELECT request_uuid, event_type, created_at
  FROM data_project_activity_events e
  JOIN data_projects p ON p.id = e.project_id
  WHERE e.request_uuid IS NOT NULL
    AND (:org_id IS NULL OR p.organization_id = :org_id)
    AND e.event_type IN ('PREVIEW_READY','FEEDBACK_REQUESTED','CHANGE_APPROVED')
)
-- per request: count PREVIEW_READY before first CHANGE_APPROVED; first-pass if count = 1
SELECT AVG(CASE WHEN preview_before_approve = 1 THEN 1.0 ELSE 0.0 END)
FROM request_preview_stats
WHERE first_approve_at >= :from AND first_approve_at < :to;`,
    { numerator: 'Requests approved on the first preview', denominator: 'Requests that got a client review in the window' },
  ),

  time_to_preview: R(
    'median milliseconds',
    [
      't0 = COALESCE(CHANGE_STARTED created_at, request.created_at, first Builder session created_at).',
      't1 = PREVIEW_READY event created_at OR data_ai_preview_instances.updated_at when status first became ready (if you only have current status, use created_at of the ready instance as a proxy).',
      'Per request_uuid, first t1 − t0. Median. Drop rows with t1 < t0.',
    ],
    `-- time_to_preview
WITH ready AS (
  SELECT request_uuid, MIN(created_at) AS t1
  FROM data_project_activity_events
  WHERE event_type = 'PREVIEW_READY' AND request_uuid IS NOT NULL
  GROUP BY request_uuid
),
started AS (
  SELECT request_uuid, MIN(created_at) AS t0
  FROM data_project_activity_events
  WHERE event_type = 'CHANGE_STARTED' AND request_uuid IS NOT NULL
  GROUP BY request_uuid
)
SELECT (EXTRACT(EPOCH FROM percentile_cont(0.5) WITHIN GROUP (ORDER BY r.t1 - s.t0)) * 1000)::bigint
FROM ready r
JOIN started s USING (request_uuid)
JOIN data_requests req ON req.uuid = r.request_uuid
JOIN data_projects p ON p.id = req.project_id
WHERE r.t1 >= :from AND r.t1 < :to
  AND (:org_id IS NULL OR p.organization_id = :org_id);`,
    { numerator: 'Median (preview ready − change started)' },
  ),

  time_to_release: R(
    'median milliseconds',
    [
      't0 = request created_at or first CHANGE_APPROVED / client_bill_status APPROVED (document which “accepted” you use — recommend created_at for now).',
      't1 = CHANGE_RELEASED event OR data_project_releases completed_at / created_at where status completed and env_kind = production.',
      'Median t1 − t0.',
    ],
    `-- time_to_release
SELECT (EXTRACT(EPOCH FROM percentile_cont(0.5) WITHIN GROUP (
  ORDER BY rel.created_at - req.created_at
)) * 1000)::bigint
FROM data_project_releases rel
JOIN data_projects p ON p.id = rel.project_id
JOIN data_requests req ON req.project_id = p.id -- tighten with ticket/request link on the release row if present
WHERE rel.status IN ('completed','released')
  AND rel.created_at >= :from AND rel.created_at < :to
  AND (:org_id IS NULL OR p.organization_id = :org_id);`,
    { numerator: 'Median (production release − request created)' },
  ),

  active_clients: R(
    'integer distinct clients',
    [
      'A client is a user_id whose cat_roles.name is a client role on a project in the org.',
      'Active: that user authored a Mo user message OR created/touched a ticket/request in [:from,:to).',
      'COUNT(DISTINCT user_id). Do not count OWNER/TEAM_LEADER.',
    ],
    `-- active_clients
SELECT COUNT(DISTINCT ur.user_id)::int
FROM data_user_roles ur
JOIN cat_roles r ON r.id = ur.role_id
JOIN data_projects p ON p.id = ur.project_id
WHERE r.name IN (${CLIENT})
  AND (:org_id IS NULL OR p.organization_id = :org_id)
  AND (
    EXISTS (
      SELECT 1 FROM data_mo_conversation_messages m
      WHERE m.user_id = ur.user_id AND m.role = 'user'
        AND m.created_at >= :from AND m.created_at < :to
    )
    OR EXISTS (
      SELECT 1 FROM data_requests req
      WHERE req.project_id = p.id AND req.created_at >= :from AND req.created_at < :to
    )
  );`,
    { numerator: 'Distinct audience-role users with activity in the window' },
  ),

  pm_touches_per_request: R(
    'float average',
    [
      'Denominator: requests with created_at or updated in window (completed + open).',
      'A PM touch is ANY of: assignee row whose user has role IN (OWNER, TEAM_LEADER) and user_started_at in window; data_mo_conversation_messages from that user; data_assignation_logs where NOT is_automated and actor is PM.',
      'Count touches per request_id, then AVG. Exclude production-approval-only later if you tag those events.',
    ],
    `-- pm_touches_per_request
WITH reqs AS (
  SELECT r.id FROM data_requests r
  JOIN data_projects p ON p.id = r.project_id
  WHERE r.created_at >= :from AND r.created_at < :to
    AND (:org_id IS NULL OR p.organization_id = :org_id)
),
touches AS (
  SELECT tk.request_id, COUNT(*) AS n
  FROM data_tickets tk
  JOIN data_ticket_user_assignees a ON a.ticket_id = tk.id
  JOIN data_user_roles ur ON ur.id = a.user_role_id
  JOIN cat_roles r ON r.id = ur.role_id
  WHERE r.name IN (${PM})
    AND a.user_started_at >= :from AND a.user_started_at < :to
  GROUP BY tk.request_id
)
SELECT COALESCE(AVG(COALESCE(t.n, 0)), 0) FROM reqs LEFT JOIN touches t ON t.request_id = reqs.id;`,
    { numerator: 'PM assignee starts + PM messages + manual assignment logs', denominator: 'Requests in window' },
  ),

  requests_per_pm: R(
    'float requests / PM FTE / month',
    [
      'Completed = data_requests.status = COMPLETED with delivery_on (or updated_at) in window.',
      'PM FTE = COUNT(DISTINCT user_id) with a PM touch in the window (start here; later replace with billed seats).',
      'Scale to 30 days: completed * (30 / window_days) / NULLIF(pm_fte, 0).',
    ],
    `-- requests_per_pm
SELECT completed * 30.0 / EXTRACT(EPOCH FROM (:to - :from)) * 86400
     / NULLIF(pm_fte, 0)
FROM counts; -- completed, pm_fte as above`,
    { numerator: 'Completed requests annualized to 30 days', denominator: 'Distinct PMs who acted in the window' },
  ),

  developer_involvement_rate: R(
    'float 0..1',
    [
      'Implementation requests: tickets whose current or any production step maps to ai_collaborator_registry key ai_builder, OR a data_ai_chat_sessions row exists for the ticket.',
      'Developer action: data_ticket_user_assignees.user_started_at IS NOT NULL on that build step AND the user is TEAM (not client, and not solely a PM approval). Proxy until human_code_change exists: any TEAM member start on the coding step.',
      'Return requests_with_dev / implementation_requests in window (by request created or completed — pick completed for stability).',
    ],
    `-- developer_involvement_rate
WITH impl AS (
  SELECT DISTINCT tk.request_id
  FROM data_tickets tk
  JOIN data_ai_chat_sessions s ON s.ticket_id = tk.id
),
dev AS (
  SELECT DISTINCT tk.request_id
  FROM data_tickets tk
  JOIN data_ticket_user_assignees a ON a.ticket_id = tk.id
  JOIN data_user_roles ur ON ur.id = a.user_role_id
  JOIN cat_roles r ON r.id = ur.role_id
  WHERE a.user_started_at IS NOT NULL
    AND r.name NOT IN (${CLIENT})
    AND r.name NOT IN (${PM}) -- TEAM members only; tighten with step type = build if you have it
)
SELECT COUNT(*) FILTER (WHERE d.request_id IS NOT NULL)::float
     / NULLIF(COUNT(*), 0)
FROM impl i
JOIN data_requests req ON req.id = i.request_id
JOIN data_projects p ON p.id = req.project_id
LEFT JOIN dev d ON d.request_id = i.request_id
WHERE req.created_at >= :from AND req.created_at < :to
  AND (:org_id IS NULL OR p.organization_id = :org_id);`,
    { numerator: 'Implementation requests a developer started working', denominator: 'Implementation requests' },
  ),

  developer_touches_per_request: R(
    'float average',
    [
      'Same implementation-request set as developer_involvement_rate.',
      'Touches: COUNT assignee rows with user_started_at on coding steps for TEAM users + COUNT data_ai_chat_messages where role=user and author is TEAM on that ticket’s Builder session.',
      'AVG(touches) per request_id.',
    ],
    `-- developer_touches_per_request
SELECT AVG(n) FROM (
  SELECT tk.request_id, COUNT(a.id) AS n
  FROM data_tickets tk
  JOIN data_ticket_user_assignees a ON a.ticket_id = tk.id
  -- filter TEAM + started_at in window
  GROUP BY tk.request_id
) x;`,
    { numerator: 'Developer assignee starts + Builder chat user messages', denominator: 'Implementation requests' },
  ),

  ai_implementation_rate: R(
    'float 0..1',
    [
      'Accepted implementation outcomes: requests COMPLETED (or CHANGE_APPROVED) that had a Builder session.',
      'Autonomous implementation: Builder session reached deployed / sent_to_test / status indicating implementation finished AND there is NO TEAM user_started_at on the coding step. Production approval by PM does NOT disqualify.',
      'Return autonomous / accepted_implementation.',
    ],
    `-- ai_implementation_rate
WITH accepted AS (
  SELECT DISTINCT tk.id AS ticket_id, tk.request_id
  FROM data_tickets tk
  JOIN data_ai_chat_sessions s ON s.ticket_id = tk.id
  JOIN data_requests req ON req.id = tk.request_id
  WHERE req.status = 'COMPLETED'
    AND s.status IN ('deployed','sent_to_test','done')
),
human_code AS (
  SELECT DISTINCT a.ticket_id
  FROM data_ticket_user_assignees a
  JOIN data_user_roles ur ON ur.id = a.user_role_id
  JOIN cat_roles r ON r.id = ur.role_id
  WHERE a.user_started_at IS NOT NULL
    AND r.name NOT IN (${CLIENT})
    AND r.name NOT IN (${PM})
)
SELECT COUNT(*) FILTER (WHERE h.ticket_id IS NULL)::float / NULLIF(COUNT(*), 0)
FROM accepted a
LEFT JOIN human_code h ON h.ticket_id = a.ticket_id;`,
    { numerator: 'Accepted changes with Builder done and no developer coding start', denominator: 'Accepted implementation changes' },
  ),

  rework_rate: R(
    'float 0..1',
    [
      'Denominator: tickets that reached verification or sent_to_test in window.',
      'Rework: a later Builder session generating after verification_run.passed = false, OR session status needs_revision, OR ticket status left COMPLETED back to IN_PROGRESS on a build step.',
      'Distinct tickets, not extra retries. Return rework / reached_qa.',
    ],
    `-- rework_rate
SELECT COUNT(*) FILTER (WHERE v.passed IS FALSE
     OR s.status = 'needs_revision')::float
     / NULLIF(COUNT(DISTINCT s.ticket_id), 0)
FROM data_ai_chat_sessions s
LEFT JOIN data_ai_agent_runs ar ON ar.source_uuid = s.uuid
LEFT JOIN data_ai_verification_runs v ON v.id = ar.verification_run_id
JOIN data_tickets tk ON tk.id = s.ticket_id
JOIN data_projects p ON p.id = tk.project_id -- tickets may join via request
WHERE s.updated_at >= :from AND s.updated_at < :to;`,
    { numerator: 'Changes sent back to Build after QA/review', denominator: 'Changes that reached verification' },
  ),

  first_pass_verification_rate: R(
    'float 0..1',
    [
      'Grain: first data_ai_verification_runs row per ticket_id (or commit_sha if populated), ordered by created_at/id.',
      'Pass: classification = fully_verified OR passed = true (prefer classification so partial/static_only is not a pass).',
      'Window on that first run’s created_at. Return passes / first_runs.',
    ],
    `-- first_pass_verification_rate
WITH first_run AS (
  SELECT DISTINCT ON (ticket_id) id, ticket_id, classification, passed, created_at
  FROM data_ai_verification_runs
  ORDER BY ticket_id, created_at ASC, id ASC
)
SELECT COUNT(*) FILTER (WHERE classification = 'fully_verified')::float
     / NULLIF(COUNT(*), 0)
FROM first_run fr
JOIN data_tickets tk ON tk.id = fr.ticket_id
WHERE fr.created_at >= :from AND fr.created_at < :to;`,
    { numerator: 'Tickets whose FIRST verification is fully_verified', denominator: 'Tickets with at least one verification run' },
  ),

  founder_rescue_rate: R(
    'float 0..1 — PROXY, no founder_rescue column',
    [
      'Failure moment: data_ai_agent_runs.status = error OR task_outcome IN (rejected) OR data_mo_tasks.phase IN (needs_attention, failed) in window.',
      'Rescue: within 24 hours AFTER that timestamp, a user with role OWNER or TEAM_LEADER posts a Mo message on the thread, starts an assignee row, or creates a new Builder session on the same ticket.',
      'Optional later: restrict actor_id to a configured founder user id list.',
      'Return rescued_outcomes / outcomes_that_failed_or_completed (document denominator: use all completed+failed requests in window).',
    ],
    `-- founder_rescue_rate (proxy)
WITH fail AS (
  SELECT ar.ticket_id, ar.ended_at
  FROM data_ai_agent_runs ar
  WHERE (ar.status = 'error' OR ar.task_outcome IN ('rejected'))
    AND ar.ended_at >= :from AND ar.ended_at < :to
),
rescue AS (
  SELECT DISTINCT f.ticket_id
  FROM fail f
  JOIN data_ticket_user_assignees a ON a.ticket_id = f.ticket_id
  JOIN data_user_roles ur ON ur.id = a.user_role_id
  JOIN cat_roles r ON r.id = ur.role_id
  WHERE r.name IN (${PM})
    AND a.user_started_at BETWEEN f.ended_at AND f.ended_at + INTERVAL '24 hours'
)
SELECT COUNT(*)::float / NULLIF((SELECT COUNT(DISTINCT ticket_id) FROM fail), 0)
FROM rescue;`,
    { numerator: 'Failed AI outcomes a PM/owner touched within 24h', denominator: 'AI failures in the window' },
  ),

  runtime_failure_rate: R(
    'float 0..1 — infrastructure, NOT “AI was wrong”',
    [
      'Denominator: data_ai_agent_runs that finished (status IN done, error, aborted) in window.',
      'Runtime failure: status = error AND error_class is infrastructure (timeout, network, tool, preview, deploy) — exclude task_outcome = rejected with a clean technical_outcome. Also count data_ai_preview_instances.status = failed and data_ticket_deploy_events.status = failed as separate infra numerators if you want a blended rate; headline uses agent_runs only.',
      'Return runtime_fail / finished_runs. Keep this split from agent_false_success_rate.',
    ],
    `-- runtime_failure_rate
SELECT COUNT(*) FILTER (
  WHERE status = 'error'
    AND COALESCE(error_class, '') ~* 'timeout|network|tool|preview|deploy|runtime'
)::float / NULLIF(COUNT(*) FILTER (WHERE status IN ('done','error','aborted')), 0)
FROM data_ai_agent_runs ar
JOIN data_projects p ON p.id = ar.project_id
WHERE ar.ended_at >= :from AND ar.ended_at < :to
  AND (:org_id IS NULL OR p.organization_id = :org_id);`,
    { numerator: 'Finished runs whose error_class is infrastructure', denominator: 'Finished agent runs' },
  ),

  cost_per_accepted_outcome: R(
    'USD float — NEVER use credits (Mo has a 3× multiplier)',
    [
      'Accepted outcomes: data_requests.status = COMPLETED in window (delivery_on).',
      'AI $: SUM(data_ai_agent_runs.usd_cost) for all runs whose ticket_id IN tickets of that request (join tickets.request_id). Include retries.',
      'Human $: SUM work hours on assignees × loaded rate (org setting; skip until it exists — return AI-only and set a flag).',
      'Return SUM(ai_usd [+ human_usd]) / COUNT(accepted requests).',
    ],
    `-- cost_per_accepted_outcome (AI only until loaded rates exist)
WITH accepted AS (
  SELECT r.id
  FROM data_requests r
  JOIN data_projects p ON p.id = r.project_id
  WHERE r.status = 'COMPLETED'
    AND COALESCE(r.delivery_on, r.updated_at) >= :from
    AND COALESCE(r.delivery_on, r.updated_at) < :to
    AND (:org_id IS NULL OR p.organization_id = :org_id)
)
SELECT SUM(ar.usd_cost) / NULLIF(COUNT(DISTINCT a.id), 0)
FROM accepted a
JOIN data_tickets tk ON tk.request_id = a.id
JOIN data_ai_agent_runs ar ON ar.ticket_id = tk.id;`,
    { numerator: 'SUM usd_cost of every agent run on tickets of accepted requests', denominator: 'Completed requests' },
  ),
}

function fallback(m: MetricDef): MetricRecipe {
  const unitHint: Record<string, string> = {
    percent: 'float 0..1',
    count: 'integer',
    duration_ms: 'median milliseconds',
    usd: 'USD float (use usd_cost, not credits)',
    ratio: 'float',
    per_fte: 'float per FTE',
  }
  return {
    returns: unitHint[m.unit] || 'number',
    numerator: m.formula,
    steps: [
      `Grain: ${m.grain}.`,
      m.calculate,
      `Org/window: join to data_projects, filter created/ended_at BETWEEN :from AND :to, optional organization_id = :org_id.`,
      `Sources: ${m.sources.join(', ')}.`,
      m.gaps ? `Gap to close: ${m.gaps}` : 'No extra event required if the sources above are enough.',
      `API field: metrics['${m.id}'].value`,
    ],
    sql: `-- ${m.id}
-- ${m.formula}
-- TODO implement from:
--   ${m.sources.join('\n--   ')}
-- ${m.calculate}
SELECT NULL::float AS value; -- replace`,
  }
}

export function recipeFor(m: MetricDef): MetricRecipe {
  return RECIPES[m.id] || fallback(m)
}

export const RECIPE_BINDINGS = `Bind in every query:
  :from   timestamptz  -- window start, inclusive
  :to     timestamptz  -- window end, exclusive
  :org_id int NULL     -- data_projects.organization_id; NULL = all orgs

Client roles: CLIENT, CLIENT_GUEST, GUEST, STAKEHOLDER, PARTICIPANT
PM roles:     OWNER, TEAM_LEADER
Cost:         data_ai_agent_runs.usd_cost — never credits (Mo has a 3× multiplier)
Grain:        one client outcome (objective_id / request), never an agent run`

export function recipeMarkdown(m: MetricDef): string {
  const r = recipeFor(m)
  const lines = [
    `# ${m.name}`,
    `id: ${m.id}`,
    `API: GET /admin/metrics → metrics['${m.id}'].value`,
    `Returns: ${r.returns}`,
    r.numerator ? `Numerator: ${r.numerator}` : '',
    r.denominator ? `Denominator: ${r.denominator}` : '',
    `Grain: ${m.grain}`,
    `Wire: ${m.wire}`,
    `Catalog formula: ${m.formula}`,
    '',
    RECIPE_BINDINGS,
    '',
    '## Steps',
    ...r.steps.map((s, i) => `${i + 1}. ${s}`),
    '',
    '## SQL',
    '```sql',
    r.sql.trim(),
    '```',
    '',
    '## Sources',
    ...m.sources.map((s) => `- ${s}`),
  ]
  if (m.gaps) lines.push('', '## Gap', m.gaps)
  return lines.filter((line) => line !== '').join('\n')
}
